/**
 * A SAÍDA PARA A OBRA, e o que a mesa cobra dela (0112).
 *
 * Funções puras, aqui e não na tela, porque a mesa e o celular mostram a mesma
 * lista e o mesmo corte: duas cópias da regra divergiriam na primeira mudança,
 * e aí a mesa cobraria um turno que o celular não cobra.
 */

/**
 * A PARTIR DE QUANTAS HORAS sem apontamento a mesa cobra.
 *
 * Seis, e o número é escolha — está escrito na tela junto da lista, porque
 * indicador cujo critério não aparece é número que ninguém confere.
 *
 * Por que não menos: o turno dura o dia, e o apontamento só sai quando o
 * aparelho reencontra a rede — cobrar com duas horas acusaria toda máquina que
 * está simplesmente trabalhando sem sinal, e lista que acusa quem está certo é
 * lista que ninguém abre na segunda semana.
 *
 * Por que não mais: oito ou dez já é o fim do turno, e aí a ligação para a
 * frente de serviço chega depois de a pessoa ir para casa com o aparelho.
 */
export const SEM_REPORTAR_COBRAR_H = 6;

/** Acima disto o valor vira vermelho: o turno inteiro passou sem notícia. */
const SEM_REPORTAR_GRAVE_H = 12;

export interface SaidaParaAObra {
  readonly equipamentoId: string;
  readonly equipamento: string;
  readonly equipamentoDescricao: string;
  /** `aaaa-mm-dd`, o dia de operação do pacote. */
  readonly dia: string;
  readonly levadoEm: string;
  readonly saidasNoDia: number;
  readonly horasSemReportar: number;
  readonly reportou: boolean;
  /**
   * De onde veio a ultima saida do dia (0114): `turno` e o turno iniciado no
   * aparelho; `pacote`, o pacote baixado pela versao anterior do app.
   */
  readonly origem: 'turno' | 'pacote';
}

/**
 * A frase da saida, a mesma na mesa e no celular. "Turno iniciado" nao e
 * "Levou o pacote": desde a 0114 a saida e o turno comecando, e a hora e a do
 * inicio do turno — e dela que contam as horas sem reportar.
 */
export function descreverSaida(s: Pick<SaidaParaAObra, 'origem' | 'levadoEm'>): string {
  const hora = new Date(s.levadoEm).toLocaleTimeString('pt-BR', {
    timeZone: 'America/Sao_Paulo',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  });
  return s.origem === 'turno' ? `Turno iniciado às ${hora}` : `Levou o pacote às ${hora}`;
}

/** "6,5 h sem reportar" — com vírgula decimal e a unidade por extenso (p.24). */
export function horasSemReportar(horas: number): string {
  return `${horas.toFixed(1).replace('.', ',')} h sem reportar`;
}

/**
 * A cor do valor. Preto é o padrão do manual; colorir só quando o próprio
 * valor é o alerta (R02) — e aqui ele é, a partir do corte.
 */
export function corDasHorasSemReportar(horas: number): string {
  if (horas >= SEM_REPORTAR_GRAVE_H) return 'text-parada-texto';
  if (horas >= SEM_REPORTAR_COBRAR_H) return 'text-standby-texto';
  return 'text-tinta';
}

/** O que a mesa cobra: saiu, não reportou, e já passou do corte. */
export function aCobrar(saidas: readonly SaidaParaAObra[]): readonly SaidaParaAObra[] {
  return saidas.filter((s) => !s.reportou && s.horasSemReportar >= SEM_REPORTAR_COBRAR_H);
}
